import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  fetchAllReportsForCoach, buildQuantitativeSnapshot, createReport, updateReport,
  type ReportType, type TransformationReportWithRespondent,
} from '../../lib/transformation-reports';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { FileText, Calendar, Search, Eye, User, Plus, Send, ChevronDown } from '../../components/icons';

const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  programme_end: 'Programme End',
};

const REPORT_TYPE_COLORS: Record<ReportType, string> = {
  weekly: 'bg-blue-100 text-blue-700',
  monthly: 'bg-sage-100 text-sage-700',
  programme_end: 'bg-amber-100 text-amber-700',
};

interface Respondent {
  id: string;
  name: string;
  email: string;
}

function defaultDateRange(type: ReportType): { start: string; end: string } {
  const today = new Date();
  const end = today.toISOString().split('T')[0];
  const start = new Date(today);
  if (type === 'weekly') start.setDate(today.getDate() - 7);
  else if (type === 'monthly') start.setMonth(today.getMonth() - 1);
  else start.setMonth(today.getMonth() - 3);
  return { start: start.toISOString().split('T')[0], end };
}

async function sendReportEmail(
  recipientEmail: string,
  recipientName: string,
  coachName: string,
  reportTitle: string,
  authToken: string,
) {
  const reportUrl = `${window.location.origin}/dashboard/reports`;
  await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      type: 'transformation-report',
      recipientEmail,
      recipientName,
      coachName,
      reportTitle,
      reportUrl,
    }),
  });
}

export function AllTransformationReports() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [reports, setReports] = useState<TransformationReportWithRespondent[]>([]);
  const [respondents, setRespondents] = useState<Respondent[]>([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [coachName, setCoachName] = useState('');

  // Generate form state
  const [selectedRespondentId, setSelectedRespondentId] = useState('');
  const [reportType, setReportType] = useState<ReportType>('monthly');
  const [formStart, setFormStart] = useState('');
  const [formEnd, setFormEnd] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [sendToClient, setSendToClient] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');

  // Per-report send state
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;
    const { start, end } = defaultDateRange('monthly');
    setFormStart(start);
    setFormEnd(end);

    const init = async () => {
      const { data: coachData } = await (supabase as any)
        .from('coaches')
        .select('id, users!coaches_user_id_fkey(full_name)')
        .eq('user_id', user.id)
        .maybeSingle();

      const coachId = coachData?.id;
      setCoachName((coachData as any)?.users?.full_name || '');

      const [allReports, { data: respondentRows }] = await Promise.all([
        fetchAllReportsForCoach(user.id),
        coachId
          ? (supabase as any)
              .from('respondents')
              .select('user_id, users!respondents_user_id_fkey(full_name, email)')
              .eq('coach_id', coachId)
          : Promise.resolve({ data: [] }),
      ]);

      setReports(allReports);
      setRespondents(
        ((respondentRows || []) as any[]).map((r: any) => ({
          id: r.user_id,
          name: r.users?.full_name || r.users?.email || r.user_id,
          email: r.users?.email || '',
        })),
      );
      setIsLoading(false);
    };
    init();
  }, [user]);

  const handleTypeChange = (type: ReportType) => {
    const { start, end } = defaultDateRange(type);
    setReportType(type);
    setFormStart(start);
    setFormEnd(end);
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedRespondentId) return;
    setGenerating(true);
    setGenerateError('');
    try {
      const respondent = respondents.find(r => r.id === selectedRespondentId)!;
      const periodStart = new Date(formStart);
      const periodEnd = new Date(formEnd);
      const title = formTitle.trim() ||
        `${REPORT_TYPE_LABELS[reportType]} Report — ${periodStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} to ${periodEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;

      const quantitative = await buildQuantitativeSnapshot(selectedRespondentId, periodStart, periodEnd);
      const report = await createReport(user.id, selectedRespondentId, reportType, periodStart, periodEnd, title, quantitative);
      if (!report) throw new Error('Failed to create report');

      if (sendToClient) {
        await updateReport(report.id, { is_published: true });
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          await sendReportEmail(respondent.email, respondent.name, coachName, title, session.access_token);
        }
        setSentIds(prev => new Set(prev).add(report.id));
      }

      const enriched: TransformationReportWithRespondent = {
        ...report,
        is_published: sendToClient ? true : report.is_published,
        respondent_name: respondent.name,
        respondent_email: respondent.email,
      };
      setReports(prev => [enriched, ...prev]);
      setShowForm(false);
      setFormTitle('');
      setSendToClient(false);
    } catch (err) {
      setGenerateError('Failed to generate report. Please try again.');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async (report: TransformationReportWithRespondent) => {
    if (!user) return;
    setSendingId(report.id);
    try {
      if (!report.is_published) {
        await updateReport(report.id, { is_published: true });
        setReports(prev => prev.map(r => r.id === report.id ? { ...r, is_published: true } : r));
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        await sendReportEmail(report.respondent_email, report.respondent_name, coachName, report.title, session.access_token);
      }
      setSentIds(prev => new Set(prev).add(report.id));
    } catch (err) {
      console.error('Send failed', err);
    } finally {
      setSendingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter(
      r => r.respondent_name.toLowerCase().includes(q) || r.title.toLowerCase().includes(q),
    );
  }, [reports, search]);

  if (isLoading) return <LoadingSpinner message="Loading reports..." />;

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="freud-heading-xl text-chocolate-900">Transformation Reports</h1>
            <p className="text-sm text-chocolate-500">All reports across your clients</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          className="freud-btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Generate Report
          {showForm ? <ChevronDown className="w-4 h-4 rotate-180" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Generate form */}
      {showForm && (
        <div className="freud-card-elevated p-6 mb-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4">Generate New Report</h2>
          <form onSubmit={handleGenerate} className="space-y-4">
            <div>
              <label className="freud-label">Client</label>
              <select
                className="freud-input"
                value={selectedRespondentId}
                onChange={e => setSelectedRespondentId(e.target.value)}
                required
              >
                <option value="">Select a client...</option>
                {respondents.map(r => (
                  <option key={r.id} value={r.id}>{r.name} — {r.email}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="freud-label">Report Type</label>
              <div className="flex gap-2">
                {(['weekly', 'monthly', 'programme_end'] as ReportType[]).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => handleTypeChange(t)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      reportType === t
                        ? 'border-sage-500 bg-sage-50 text-sage-700'
                        : 'border-chocolate-200 text-chocolate-600 hover:border-chocolate-400'
                    }`}
                  >
                    {REPORT_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="freud-label">Period Start</label>
                <input type="date" className="freud-input" value={formStart} onChange={e => setFormStart(e.target.value)} required />
              </div>
              <div>
                <label className="freud-label">Period End</label>
                <input type="date" className="freud-input" value={formEnd} onChange={e => setFormEnd(e.target.value)} required />
              </div>
            </div>

            <div>
              <label className="freud-label">Report Title (optional)</label>
              <input
                className="freud-input"
                placeholder="Auto-generated if left blank"
                value={formTitle}
                onChange={e => setFormTitle(e.target.value)}
              />
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sendToClient}
                onChange={e => setSendToClient(e.target.checked)}
                className="w-4 h-4 rounded border-chocolate-300 text-sage-600"
              />
              <span className="text-sm text-chocolate-700">Publish and send to client immediately</span>
            </label>

            {generateError && (
              <p className="text-sm text-red-600">{generateError}</p>
            )}

            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={generating} className="freud-btn-primary flex items-center gap-2">
                {generating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Generating...
                  </>
                ) : 'Generate Report'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setGenerateError(''); }} className="freud-btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chocolate-400" />
        <input
          className="freud-input pl-9"
          placeholder="Search by client name or report title..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Report list */}
      {filtered.length === 0 ? (
        <div className="freud-card-elevated p-12 text-center">
          <FileText className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-chocolate-500">
            {search ? 'No reports match your search.' : 'No transformation reports yet.'}
          </p>
          {!search && (
            <p className="text-sm text-chocolate-400 mt-1">
              Use the Generate Report button above to create your first report.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(report => {
            const justSent = sentIds.has(report.id);
            return (
              <div key={report.id} className="freud-card-elevated p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${REPORT_TYPE_COLORS[report.report_type]}`}>
                      {REPORT_TYPE_LABELS[report.report_type]}
                    </span>
                    {report.is_published && (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Published</span>
                    )}
                    {report.ai_narrative && (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">AI Narrative</span>
                    )}
                  </div>
                  <p className="font-medium text-chocolate-900 truncate">{report.title}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-chocolate-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {report.respondent_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(report.period_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} –{' '}
                      {new Date(report.period_end).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span>Created {new Date(report.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Link
                    to={`/coach/proof/${report.respondent_user_id}/reports/${report.id}`}
                    className="p-2 rounded-lg text-chocolate-400 hover:bg-sage-50 hover:text-sage-600 transition-colors"
                    title="View report"
                  >
                    <Eye className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => handleSend(report)}
                    disabled={sendingId === report.id}
                    title={justSent ? 'Sent! Click to resend' : 'Send to client'}
                    className={`p-2 rounded-lg transition-colors ${
                      justSent
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-chocolate-400 hover:bg-amber-50 hover:text-amber-600'
                    }`}
                  >
                    {sendingId === report.id ? (
                      <div className="w-4 h-4 border-2 border-chocolate-300 border-t-chocolate-600 rounded-full animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
